import { createParser } from "eventsource-parser";

export type SseEvent = { event?: string; data?: string };
export type SseHandler = (event: SseEvent) => void;

/** Thrown when the SSE request gets a non-2xx response. ``status`` lets
 *  callers distinguish an auth failure (401/403 — don't reconnect, the
 *  cookie is dead) from a transient transport blip (reconnect). */
export class SseHttpError extends Error {
  readonly status: number;
  constructor(status: number) {
    super(`SSE request failed: ${status}`);
    this.name = "SseHttpError";
    this.status = status;
  }
}

export async function streamSse(
  url: string,
  onEvent: SseHandler,
  signal?: AbortSignal,
): Promise<void> {
  const init: RequestInit = {
    method: "GET",
    headers: { Accept: "text/event-stream" },
    credentials: "include",
  };
  if (signal) init.signal = signal;
  const response = await fetch(url, init);
  if (!response.ok || !response.body) {
    throw new SseHttpError(response.status);
  }
  const parser = createParser({
    onEvent: (event) => {
      onEvent({ event: event.event, data: event.data });
    },
  });
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  while (!signal?.aborted) {
    const { value, done } = await reader.read();
    if (done) return;
    parser.feed(decoder.decode(value, { stream: true }));
  }
}
